import axios, { AxiosError, AxiosHeaders, type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { getConfig } from '@/config'
import {
  getAccessToken,
  clearLoginFailure,
  getAuthRequestContext,
  handleAuthorizationFailure,
  notifyAuthorizationRequired,
} from '@/auth/session'

export interface ApiRequestConfig extends AxiosRequestConfig {
  skipAuthRedirect?: boolean
  authVersion?: number
  promptForAuthentication?: boolean
}

export class ApiError extends Error {
  status?: number
  code?: string
  data?: unknown
  isNetworkError: boolean

  constructor(message: string, options: {
    status?: number
    code?: string
    data?: unknown
    isNetworkError?: boolean
  } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = options.status
    this.code = options.code
    this.data = options.data
    this.isNetworkError = options.isNetworkError ?? false
  }
}

let apiClient: AxiosInstance | null = null

function normalizeApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error
  }

  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    const data = error.response?.data
    const fallbackMessage = status
      ? `Request failed with status ${status}`
      : 'Network request failed'
    const responseMessage =
      typeof data === 'object' && data !== null && 'message' in data && typeof data.message === 'string'
        ? data.message
        : undefined
    const responseError =
      typeof data === 'object' && data !== null && 'error' in data && typeof data.error === 'string'
        ? data.error
        : undefined

    return new ApiError(responseMessage ?? responseError ?? error.message ?? fallbackMessage, {
      status,
      code: error.code,
      data,
      isNetworkError: !error.response,
    })
  }

  if (error instanceof Error) {
    return new ApiError(error.message)
  }

  return new ApiError('Unknown API error')
}

function buildHeaders(headers?: AxiosRequestConfig['headers']): AxiosHeaders {
  const normalized = AxiosHeaders.from(headers as unknown as string | AxiosHeaders | undefined)

  const accessToken = getAccessToken()
  if (accessToken && !normalized.has('Authorization')) {
    normalized.set('Authorization', `Bearer ${accessToken}`)
  }

  return normalized
}

function hasAuthorizationHeader(headers: AxiosRequestConfig['headers'] | Headers | undefined): boolean {
  if (!headers) return false

  if (headers instanceof Headers) {
    return headers.has('Authorization')
  }

  const normalized = AxiosHeaders.from(headers as unknown as string | AxiosHeaders | undefined)
  return normalized.has('Authorization')
}

/**
 * Maps an HTTP status onto the auth signal the UI reacts to. Exported for unit
 * testing: the branches are pure, and covering them in a browser test would
 * cost seconds to assert what takes milliseconds here.
 */
export interface AuthorizationResponse {
  status: number | undefined
  hasAuthHeader: boolean
  skipAuthRedirect?: boolean
  promptForAuthentication?: boolean
  authVersion?: number
}

export function handleAuthorizationResponse({
  status,
  hasAuthHeader,
  skipAuthRedirect,
  promptForAuthentication,
  authVersion,
}: AuthorizationResponse) {
  if (skipAuthRedirect || (authVersion !== undefined && authVersion !== getAuthRequestContext().version)) return

  if (status && status >= 200 && status < 300 && hasAuthHeader) clearLoginFailure()
  if (status !== 401) return

  if (hasAuthHeader) {
    handleAuthorizationFailure(status)
    return
  }

  if (promptForAuthentication) {
    notifyAuthorizationRequired()
  }
}

export function createApiHeaders(headers?: AxiosRequestConfig['headers']): Headers {
  const normalized = buildHeaders(headers)
  return new Headers(normalized.toJSON() as HeadersInit)
}

export function buildApiUrl(path: string): string {
  return new URL(`${getConfig().apiBaseUrl}${path}`, window.location.origin).toString()
}

async function fetchWithSharedHandling(
  input: string,
  init: RequestInit & Pick<ApiRequestConfig, 'skipAuthRedirect' | 'promptForAuthentication'> = {},
): Promise<Response> {
  const { skipAuthRedirect, promptForAuthentication, ...requestInit } = init
  const authContext = getAuthRequestContext()
  const requestHeaders = createApiHeaders(init.headers as AxiosRequestConfig['headers'])
  const response = await fetch(buildApiUrl(input), {
    ...requestInit,
    headers: requestHeaders,
    signal: skipAuthRedirect ? requestInit.signal : AbortSignal.any([authContext.signal, ...(requestInit.signal ? [requestInit.signal] : [])]),
  })

  if (!skipAuthRedirect && authContext.version !== getAuthRequestContext().version) {
    throw new DOMException('The request belongs to an earlier session.', 'AbortError')
  }
  handleAuthorizationResponse({
    status: response.status,
    authVersion: authContext.version,
    hasAuthHeader: hasAuthorizationHeader(requestHeaders),
    skipAuthRedirect,
    promptForAuthentication,
  })

  return response
}

export async function fetchApi(
  input: string,
  init: RequestInit & Pick<ApiRequestConfig, 'skipAuthRedirect' | 'promptForAuthentication'> = {},
): Promise<Response> {
  return fetchWithSharedHandling(input, init)
}

export async function fetchSseApi(
  input: string,
  init: RequestInit & Pick<ApiRequestConfig, 'skipAuthRedirect' | 'promptForAuthentication'> = {},
): Promise<Response> {
  const headers = AxiosHeaders.from(init.headers as unknown as string | AxiosHeaders | undefined)
  if (!headers.has('Accept')) {
    headers.set('Accept', 'text/event-stream')
  }

  return fetchWithSharedHandling(input, {
    ...init,
    headers,
  })
}

function createApiClient(): AxiosInstance {
  const client = axios.create({
    timeout: 30000,
  })

  client.interceptors.request.use((config) => {
    const nextConfig = config as typeof config & ApiRequestConfig
    nextConfig.baseURL = getConfig().apiBaseUrl
    nextConfig.headers = buildHeaders(config.headers)
    const authContext = getAuthRequestContext()
    nextConfig.authVersion = authContext.version
    if (!nextConfig.skipAuthRedirect) {
      nextConfig.signal = AbortSignal.any([authContext.signal, ...(config.signal ? [config.signal as AbortSignal] : [])])
    }
    return nextConfig
  })

  client.interceptors.response.use(
    response => {
      const config = response.config as ApiRequestConfig
      if (!config.skipAuthRedirect && config.authVersion !== getAuthRequestContext().version) {
        throw new DOMException('The request belongs to an earlier session.', 'AbortError')
      }
      handleAuthorizationResponse({ status: response.status, hasAuthHeader: hasAuthorizationHeader(config.headers), skipAuthRedirect: config.skipAuthRedirect, authVersion: config.authVersion })
      return response
    },
    (error: AxiosError) => {
      const nextError = normalizeApiError(error)
      const config = error.config as ApiRequestConfig | undefined

      handleAuthorizationResponse({
        status: nextError.status,
        authVersion: config?.authVersion,
        hasAuthHeader: hasAuthorizationHeader(config?.headers),
        skipAuthRedirect: config?.skipAuthRedirect,
        promptForAuthentication: config?.promptForAuthentication,
      })

      return Promise.reject(nextError)
    },
  )

  return client
}

export function getApiClient(): AxiosInstance {
  if (!apiClient) {
    apiClient = createApiClient()
  }
  return apiClient
}
